import type { ExamType } from "../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";

export { EXAM_ICONS } from "./epocus-icons";

export const EXAM_LABELS: Record<ExamType, string> = {
  efast: "eFAST",
  cardiac: "Focused Cardiac",
  lung: "Lung",
  bladder: "Bladder",
  renal: "Renal",
  aorta: "Aorta",
  pregnancy: "Pregnancy",
};

export const EXAM_TICKET_IDS: Record<ExamType, string> = {
  efast: "PNE-189",
  cardiac: "PNE-190",
  lung: "PNE-191",
  bladder: "PNE-192",
  renal: "PNE-193",
  aorta: "PNE-194",
  pregnancy: "PNE-387",
};

export type DeviceOption = { id: string; label: string };

/**
 * Ids must match `epocusDeviceCatalogue` in api-imaging-study: a program's
 * `enabledDeviceIds` is filtered against these, and an id that matches nothing
 * narrows to an empty list, which reads as "unconfigured" and offers every
 * device instead.
 */
export const DEVICE_CATALOGUE: readonly DeviceOption[] = [
  { id: "ge-vscan-air", label: "GE Vscan Air" },
  { id: "butterfly-iq-plus", label: "Butterfly IQ+" },
  { id: "sonostar", label: "Sonostar" },
  { id: "echonous-kosmos-torso-one", label: "Kosmos Torso-One" },
  { id: "echonous-kosmos-lexsa", label: "Kosmos Lexsa" },
];

/**
 * Ids no longer in the catalogue, kept so an encounter written against one
 * still renders a device name rather than a raw id.
 */
const RETIRED_DEVICE_LABELS: Readonly<Record<string, string>> = {
  "kosmos-torso-one": "Kosmos Torso-One",
  "kosmos-lexsa": "Kosmos Lexsa",
};

export const DEVICE_LABELS: Readonly<Record<string, string>> = {
  ...RETIRED_DEVICE_LABELS,
  ...Object.fromEntries(
    DEVICE_CATALOGUE.map((device) => [device.id, device.label]),
  ),
};

/**
 * Default device for a new encounter by precedence:
 * 1. the clinician's stored preference, if it's still a valid option;
 * 2. the sole option when the list leaves no choice;
 * 3. otherwise null (the clinician must choose).
 * A stale/unknown preferred id is ignored so a removed device never lingers.
 */
export const getDefaultDeviceId = (
  options: readonly DeviceOption[],
  preferredId?: string | null,
): string | null => {
  if (preferredId && options.some((option) => option.id === preferredId)) {
    return preferredId;
  }
  return options.length === 1 ? options[0].id : null;
};
